/**
 * 発注一覧テーブル
 *
 * 設計書 05-frontend-design.md §5.2 整合。
 * カラム定義 + 操作ボタン(`isReversible` / `isCancellable` フラグ + 権限ヘルパーで分岐)。
 * フラグ判定はサーバー暫定値を素通し(設計書 D-B、フロント側で再判定しない)。
 */
import { Button, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatDate, formatDateTimeShort } from '@/shared/utils/dateFormatter';
import { FullText } from '@/shared/components/Display/FullText';
import {
  PURCHASE_ORDER_STATUS_DISPLAY_NAME,
  PURCHASE_ORDER_STATUS_TAG_COLOR,
} from '../types/purchaseOrder';
import type {
  PurchaseOrderPagination,
  PurchaseOrderViewModel,
  PurchaseOrderStatusValue,
} from '../types/purchaseOrder';

const TABLE_SCROLL_X_PX = 1000;
const COL_WIDTH_ID_PX = 80;
const COL_WIDTH_SITE_PX = 100;
const COL_WIDTH_DATE_PX = 130;
const COL_WIDTH_MATERIAL_PX = 110;
const COL_WIDTH_QUANTITY_PX = 70;
const COL_WIDTH_EXPECTED_PX = 110;
const COL_WIDTH_STATUS_PX = 100;
const COL_WIDTH_USER_PX = 100;
const COL_WIDTH_ACTIONS_PX = 220;
/** 備考が未入力のときの表示（従来どおり空欄） */
const EMPTY_ORDER_NOTE_PLACEHOLDER = '';
const OVERDUE_CELL_VERTICAL_GAP_PX = 2;

interface Props {
  readonly items: readonly PurchaseOrderViewModel[];
  readonly pagination: PurchaseOrderPagination;
  readonly pageSize: number;
  readonly isLoading: boolean;
  /** MCM/DEV のみ true(現場列を表示) */
  readonly showsSiteColumn: boolean;
  readonly canConfirmArrival: boolean;
  readonly canReverseArrival: boolean;
  readonly canCancelPurchaseOrder: boolean;
  readonly onConfirmArrival: (params: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onReverseArrival: (params: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onCancelPurchaseOrder: (params: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onPageChange: (params: { readonly page: number }) => void;
}

/**
 * Status を Tag(色付き)で描画
 */
function renderStatusTag(params: {
  readonly status: PurchaseOrderStatusValue;
}): React.ReactElement {
  return (
    <Tag color={PURCHASE_ORDER_STATUS_TAG_COLOR[params.status]}>
      {PURCHASE_ORDER_STATUS_DISPLAY_NAME[params.status]}
    </Tag>
  );
}

/**
 * 着荷予定日セル(超過時は赤字 + isOverdue バッジ)
 *
 * 列幅 110px に収まるよう、遅延時は日付と Tag を縦積みする。
 * 通常時は string、遅延時は Space を返すため戻り型は ReactNode。
 */
function renderExpectedArrivalCell(params: {
  readonly date: Date;
  readonly isOverdue: boolean;
}): React.ReactNode {
  const dateText = formatDate(params.date);
  if (params.isOverdue) {
    return (
      <Space direction="vertical" size={OVERDUE_CELL_VERTICAL_GAP_PX}>
        <span style={{ color: 'red' }}>{dateText}</span>
        <Tag color="error" style={{ margin: 0 }}>
          遅延
        </Tag>
      </Space>
    );
  }
  return dateText;
}

/**
 * 操作カラムのボタン列を描画
 *
 * `isReversible` / `isCancellable` はサーバー暫定値を素通し(設計書 D-B)。
 */
function renderActionsCell(params: {
  readonly record: PurchaseOrderViewModel;
  readonly canConfirmArrival: boolean;
  readonly canReverseArrival: boolean;
  readonly canCancelPurchaseOrder: boolean;
  readonly onConfirmArrival: (innerParams: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onReverseArrival: (innerParams: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onCancelPurchaseOrder: (innerParams: {
    readonly target: PurchaseOrderViewModel;
  }) => void;
}): React.ReactElement {
  const { record } = params;
  // TODO: ARRIVED / CANCELLED 行の `[詳細]` ボタンは後続PRで追加(設計書 §5.2)。
  // `getPurchaseOrderById` API は既存だが、詳細表示モーダルが未実装のため別 PR 委譲。
  return (
    <Space size="small">
      {record.status === 'ORDERED' && params.canConfirmArrival && (
        <Button
          size="small"
          type="primary"
          onClick={() => params.onConfirmArrival({ target: record })}
        >
          入庫確認
        </Button>
      )}
      {record.status === 'ORDERED' &&
        record.isCancellable &&
        params.canCancelPurchaseOrder && (
          <Button
            size="small"
            danger
            onClick={() => params.onCancelPurchaseOrder({ target: record })}
          >
            キャンセル
          </Button>
        )}
      {record.status === 'ARRIVED' && record.isReversible && params.canReverseArrival && (
        <Button size="small" onClick={() => params.onReverseArrival({ target: record })}>
          入庫取消
        </Button>
      )}
    </Space>
  );
}

/**
 * テーブル列定義の構築
 *
 * 権限フラグ・列表示フラグごとのカラム数差分を関数化し、テスト容易性を確保する。
 */
function buildColumns(params: {
  readonly showsSiteColumn: boolean;
  readonly canConfirmArrival: boolean;
  readonly canReverseArrival: boolean;
  readonly canCancelPurchaseOrder: boolean;
  readonly onConfirmArrival: (innerParams: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onReverseArrival: (innerParams: { readonly target: PurchaseOrderViewModel }) => void;
  readonly onCancelPurchaseOrder: (innerParams: {
    readonly target: PurchaseOrderViewModel;
  }) => void;
}): ColumnsType<PurchaseOrderViewModel> {
  const columns: ColumnsType<PurchaseOrderViewModel> = [
    {
      title: '発注ID',
      dataIndex: 'id',
      width: COL_WIDTH_ID_PX,
      render: (value: number) => `#${value}`,
    },
  ];

  if (params.showsSiteColumn) {
    columns.push({
      title: '現場',
      dataIndex: 'siteName',
      width: COL_WIDTH_SITE_PX,
    });
  }

  columns.push(
    {
      title: '発注日',
      dataIndex: 'orderedAt',
      width: COL_WIDTH_DATE_PX,
      render: (value: Date) => formatDateTimeShort(value),
    },
    {
      title: '材料',
      dataIndex: 'materialTypeName',
      width: COL_WIDTH_MATERIAL_PX,
    },
    {
      title: '発注数',
      dataIndex: 'orderedQuantity',
      width: COL_WIDTH_QUANTITY_PX,
      align: 'right',
    },
    {
      title: '入庫予定日',
      width: COL_WIDTH_EXPECTED_PX,
      render: (_value, record: PurchaseOrderViewModel) =>
        renderExpectedArrivalCell({
          date: record.expectedArrivalDate,
          isOverdue: record.isOverdue,
        }),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      width: COL_WIDTH_STATUS_PX,
      render: (value: PurchaseOrderStatusValue) => renderStatusTag({ status: value }),
    },
    {
      title: '発注者',
      dataIndex: 'orderedByUserName',
      width: COL_WIDTH_USER_PX,
    },
    {
      title: '備考',
      dataIndex: 'orderNote',
      // 2 行で打ち切ると、入庫確認画面以外（入庫済み・キャンセル済み）では全文を読めないため、全文を表示する
      render: (value: string | null) => (
        <FullText text={value} emptyPlaceholder={EMPTY_ORDER_NOTE_PLACEHOLDER} />
      ),
    },
    {
      title: '操作',
      width: COL_WIDTH_ACTIONS_PX,
      render: (_value, record: PurchaseOrderViewModel) =>
        renderActionsCell({
          record,
          canConfirmArrival: params.canConfirmArrival,
          canReverseArrival: params.canReverseArrival,
          canCancelPurchaseOrder: params.canCancelPurchaseOrder,
          onConfirmArrival: params.onConfirmArrival,
          onReverseArrival: params.onReverseArrival,
          onCancelPurchaseOrder: params.onCancelPurchaseOrder,
        }),
    }
  );

  return columns;
}

export function PurchaseOrderTable({
  items,
  pagination,
  pageSize,
  isLoading,
  showsSiteColumn,
  canConfirmArrival,
  canReverseArrival,
  canCancelPurchaseOrder,
  onConfirmArrival,
  onReverseArrival,
  onCancelPurchaseOrder,
  onPageChange,
}: Props): React.ReactElement {
  // 親側のインラインコールバックにより毎回参照同一性が崩れるため useMemo は撤去。
  // items は最大 PAGE_SIZE のため毎レンダー再構築可。
  const columns: ColumnsType<PurchaseOrderViewModel> = buildColumns({
    showsSiteColumn,
    canConfirmArrival,
    canReverseArrival,
    canCancelPurchaseOrder,
    onConfirmArrival,
    onReverseArrival,
    onCancelPurchaseOrder,
  });

  // antd Table の dataSource は型上 mutable を要求するため readonly items を spread でコピー。
  return (
    <Table<PurchaseOrderViewModel>
      rowKey="id"
      dataSource={[...items]}
      columns={columns}
      loading={isLoading}
      scroll={{ x: TABLE_SCROLL_X_PX }}
      // 備考の ellipsis を外すと既定が auto になり列幅が変わるため、従来どおり fixed を明示する
      tableLayout="fixed"
      pagination={{
        current: pagination.currentPage,
        pageSize,
        total: pagination.totalCount,
        showSizeChanger: false,
        onChange: (page: number) => onPageChange({ page }),
      }}
    />
  );
}
